package cc.easyandroid.easycompose.core.instancekeeper

import cc.easyandroid.easycompose.core.instancekeeper.InstanceKeeper.SimpleInstance
import kotlin.properties.PropertyDelegateProvider
import kotlin.properties.ReadOnlyProperty
import kotlin.reflect.typeOf

/**
 * Returns a previously stored [InstanceKeeper.Instance] of type [T] with the given key,
 * or creates and stores a new one if it doesn't exist.
 */
inline fun <T : InstanceKeeper.Instance> InstanceKeeper.getOrCreate(key: Any, factory: () -> T): T {
    @Suppress("UNCHECKED_CAST") // Assuming the type per key is always the same
    var instance: T? = get(key) as T?
    if (instance == null) {
        instance = factory()
        put(key, instance)
    }

    return instance
}

/**
 * Returns a previously stored [AutoCloseable] instance of type [T] with the given [key],
 * or creates and stores a new one if it doesn't exist.
 *
 * @param key a key to store and retrieve the instance.
 * @param factory a function creating a new instance of type [T].
 */
inline fun <T : AutoCloseable> InstanceKeeper.getOrCreateCloseable(key: Any, factory: () -> T): T =
    getOrCreate(key = key) {
        val instance = factory()

        object : InstanceKeeper.Instance {
            val instance: T = instance

            override fun onDestroy() {
                instance.close()
            }
        }
    }.instance


/**
 * A convenience function for [InstanceKeeper.getOrCreate].
 */
inline fun <T : InstanceKeeper.Instance> InstanceKeeperOwner.retainedInstance(key: Any, factory: () -> T): T =
    instanceKeeper.getOrCreate(key = key, factory = factory)

/**
 * A convenience function for [InstanceKeeper.getOrCreateCloseable].
 */
inline fun <T : AutoCloseable> InstanceKeeperOwner.retainedCloseable(key: Any, factory: () -> T): T =
    instanceKeeper.getOrCreateCloseable(key = key, factory = factory)

/**
 * Returns a previously stored instance of type [T] with the given key,
 * or creates and stores a new one if it doesn't exist.
 *
 * This overload is for simple cases when instance destroying is not required.
 */
inline fun <T> InstanceKeeper.getOrCreateSimple(key: Any, factory: () -> T): T =
    getOrCreate(key = key) { SimpleInstance(factory()) }
        .instance

/**
 * A convenience function for [InstanceKeeper.getOrCreateSimple].
 */
inline fun <T> InstanceKeeperOwner.retainedSimpleInstance(key: Any, factory: () -> T): T =
    instanceKeeper.getOrCreateSimple(key = key, factory = factory)

/**
 * Helper function for creating a
 * [delegated property]((https://kotlinlang.org/docs/delegated-properties.html)) whose value is
 * automatically retained using [InstanceKeeper].
 *
 * Example:
 *
 * ```
 *
 * class SomeLogic(override val instanceKeeper: InstanceKeeper) : InstanceKeeperOwner {
 *     private val viewModel by retainingInstance { RetainedViewModel() }
 *
 *     private class RetainedViewModel : InstanceKeeper.Instance {
 *         // ...
 *     }
 * }
 * ```
 *
 * @param key an optional key for storing and retrieving the instance. If not provided, then the
 * property name is used as a key.
 * @param factory a function creating a new instance of type [T].
 * @param T a type of the property, extends [InstanceKeeper.Instance].
 * @return [PropertyDelegateProvider] of type [T], typically used to define a delegated property.
 * @see getOrCreate
 */
@ExperimentalInstanceKeeperApi
inline fun <T : InstanceKeeper.Instance> InstanceKeeper.retainingInstance(
    key: Any? = null,
    crossinline factory: () -> T,
): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, T>> =
    PropertyDelegateProvider { _, property ->
        val instance = getOrCreate(key = key ?: "RETAINING_INSTANCE_${property.name}", factory = factory)
        ReadOnlyProperty { _, _ -> instance }
    }

/**
 * Helper function for creating a
 * [delegated property]((https://kotlinlang.org/docs/delegated-properties.html)) whose value is
 * automatically retained using [InstanceKeeper].
 *
 * Example:
 *
 * ```
 * class SomeLogic(override val instanceKeeper: InstanceKeeper) : InstanceKeeperOwner {
 *     private val viewModel by retainingInstance { RetainedViewModel() }
 *
 *     private class RetainedViewModel : InstanceKeeper.Instance {
 *         // ...
 *     }
 * }
 * ```
 *
 * @param key an optional key for storing and retrieving the instance. If not provided, then the
 * property name is used as a key.
 * @param factory a function creating a new instance of type [T].
 * @param T a type of the property, extends [InstanceKeeper.Instance].
 * @return [PropertyDelegateProvider] of type [T], typically used to define a delegated property.
 * @see getOrCreate
 */
@ExperimentalInstanceKeeperApi
inline fun <T : InstanceKeeper.Instance> InstanceKeeperOwner.retainingInstance(
    key: Any? = null,
    crossinline factory: () -> T,
): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, T>> =
    instanceKeeper.retainingInstance(key = key, factory = factory)

/**
 * Helper function for creating a
 * [delegated property]((https://kotlinlang.org/docs/delegated-properties.html)) whose value is
 * automatically retained using [InstanceKeeper].
 *
 * This overload is for simple cases when instance destroying is not required.
 *
 * Example:
 *
 * ```
 * class SomeLogic(override val instanceKeeper: InstanceKeeper) : InstanceKeeperOwner {
 *     private val viewModel by retainingSimpleInstance { RetainedClass() }
 *
 *     private class RetainedClass {
 *         // ...
 *     }
 * }
 * ```
 *
 * @param key an optional key for storing and retrieving the instance. If not provided, then the
 * property name is used as a key.
 * @param factory a function creating a new instance of type [T].
 * @param T a type of the property.
 * @return [PropertyDelegateProvider] of type [T], typically used to define a delegated property.
 * @see getOrCreateSimple
 */
@ExperimentalInstanceKeeperApi
inline fun <T> InstanceKeeper.retainingSimpleInstance(
    key: Any? = null,
    crossinline factory: () -> T,
): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, T>> =
    PropertyDelegateProvider { _, property ->
        val instance = getOrCreateSimple(key = key ?: "RETAINING_SIMPLE_INSTANCE_${property.name}", factory = factory)
        ReadOnlyProperty { _, _ -> instance }
    }

/**
 * Helper function for creating a
 * [delegated property]((https://kotlinlang.org/docs/delegated-properties.html)) whose value is
 * automatically retained using [InstanceKeeper].
 *
 * This overload is for simple cases when instance destroying is not required.
 *
 * Example:
 *
 * ```
 * class SomeLogic(override val instanceKeeper: InstanceKeeper) : InstanceKeeperOwner {
 *     private val viewModel by retainingSimpleInstance { RetainedClass() }
 *
 *     private class RetainedClass {
 *         // ...
 *     }
 * }
 * ```
 *
 * @param key an optional key for storing and retrieving the instance. If not provided, then the
 * property name is used as a key.
 * @param factory a function creating a new instance of type [T].
 * @param T a type of the property.
 * @return [PropertyDelegateProvider] of type [T], typically used to define a delegated property.
 * @see getOrCreateSimple
 */
@ExperimentalInstanceKeeperApi
inline fun <T> InstanceKeeperOwner.retainingSimpleInstance(
    key: Any? = null,
    crossinline factory: () -> T,
): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, T>> =
    instanceKeeper.retainingSimpleInstance(key = key, factory = factory)

/**
 * Helper function for creating a
 * [delegated property]((https://kotlinlang.org/docs/delegated-properties.html)) whose value is
 * automatically retained using [InstanceKeeper].
 *
 * This overload is for simple cases when instance destroying is not required.
 *
 * Example:
 *
 *
 * class SomeLogic(override val instanceKeeper: InstanceKeeper) : InstanceKeeperOwner {
 *     private val viewModel by retainingSimpleInstance { RetainedViewModel() }
 *
 *     private class RetainedViewModel : AutoCloseable {
 *         // ...
 *     }
 * }
 * ```
 *
 * @param key an optional key for storing and retrieving the instance. If not provided, then the
 * property name is used as a key.
 * @param factory a function creating a new instance of type [T].
 * @param T a type of the property, extends [AutoCloseable].
 * @return [PropertyDelegateProvider] of type [T], typically used to define a delegated property.
 * @see getOrCreateCloseable
 */
@ExperimentalInstanceKeeperApi
inline fun <T : AutoCloseable> InstanceKeeper.retainingCloseable(
    key: Any? = null,
    crossinline factory: () -> T,
): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, T>> =
    PropertyDelegateProvider { _, property ->
        val instance = getOrCreateCloseable(key = key ?: "RETAINING_CLOSEABLE_${property.name}", factory = factory)
        ReadOnlyProperty { _, _ -> instance }
    }

/**
 * Helper function for creating a
 * [delegated property]((https://kotlinlang.org/docs/delegated-properties.html)) whose value is
 * automatically retained using [InstanceKeeper].
 *
 * This overload is for simple cases when instance destroying is not required.
 *
 * Example:
 *
 * ```
 *
 * class SomeLogic(override val instanceKeeper: InstanceKeeper) : InstanceKeeperOwner {
 *     private val viewModel by retainingSimpleInstance { RetainedViewModel() }
 *
 *     private class RetainedViewModel : AutoCloseable {
 *         // ...
 *     }
 * }
 * ```
 *
 * @param key an optional key for storing and retrieving the instance. If not provided, then the
 * property name is used as a key.
 * @param factory a function creating a new instance of type [T].
 * @param T a type of the property, extends [AutoCloseable].
 * @return [PropertyDelegateProvider] of type [T], typically used to define a delegated property.
 * @see getOrCreateCloseable
 */
@ExperimentalInstanceKeeperApi
inline fun <T : AutoCloseable> InstanceKeeperOwner.retainingCloseable(
    key: Any? = null,
    crossinline factory: () -> T,
): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, T>> =
    instanceKeeper.retainingCloseable(key = key, factory = factory)



